import { Controller } from "@hotwired/stimulus"
import { confirmDialog, successDialog, errorDialog } from "../helpers/swal_helper"

// Connects to data-controller="timesheets"
export default class extends Controller {
  static targets = [
    "timesheetList",
    "historyEmpty",
    "totalHoursDisplay",
    "entryDailyHours",
    "entryStartTime",
    "entryEndTime",
    "entryAbsenceReason",
    "submitButton",
    "weekPicker",
    "modal",
    "form",
    "notes",
    "confirmButton"
  ]

  static values = {
    csrfToken: String,
    timesheetId: String,
    teacherName: String,
    weekStart: String,
    rejectUrl: String
  }

  connect() {
    this.csrfTokenValue = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    this.setupRejectionEventListeners()

    // Initialize hours calculation
    setTimeout(() => this.initializeHours(), 100)
  }

  handleTimeInput(event) {
    const input = event.target
    const entryId = input.dataset.entryId
    const row = input.closest('tr')

    this.calculateHoursForEntry(entryId)
    this.calculateTotalHours()
  }

  handleAbsenceChange(event) {
    const select = event.target
    const entryId = select.dataset.entryId
    const row = select.closest('tr')

    if (select.value) {
      const startInput = row.querySelector('[data-timesheets-target="entryStartTime"]')
      const endInput = row.querySelector('[data-timesheets-target="entryEndTime"]')
      if (startInput) startInput.value = ''
      if (endInput) endInput.value = ''
    }

    this.calculateHoursForEntry(entryId)
    this.calculateTotalHours()
  }

  handleWeekChange(event) {
    const selectedDate = event.target.value
    if (!selectedDate) return

    const date = new Date(selectedDate)

    const dayOfWeek = date.getDay()
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    date.setDate(date.getDate() + daysToMonday)

    const mondayDate = date.toISOString().split('T')[0]

    event.target.value = mondayDate

    const currentUrl = new URL(window.location)
    currentUrl.searchParams.set('week_start_at', mondayDate)
    window.location.href = currentUrl.toString()
  }

  calculateTimeForDay(dayIndex) {
    const row = this.element.querySelector(`tr[data-day-index="${dayIndex}"]`)
    if (!row) return 0

    const startInput = row.querySelector('[data-timesheets-target="entryStartTime"]')
    const endInput = row.querySelector('[data-timesheets-target="entryEndTime"]')
    const reasonSelect = row.querySelector('[data-timesheets-target="entryAbsenceReason"]')
    const hoursSpan = row.querySelector('[data-timesheets-target="entryDailyHours"]')

    let hours = 0

    const hasStartTime = startInput?.value && startInput.value.trim() !== ''
    const hasEndTime = endInput?.value && endInput.value.trim() !== ''
    const hasAbsenceReason = reasonSelect?.value && reasonSelect.value.trim() !== ''

    if (hasStartTime && hasEndTime && !hasAbsenceReason) {
      const startParts = startInput.value.split(':')
      const endParts = endInput.value.split(':')

      const startHour = parseInt(startParts[0])
      const startMin = parseInt(startParts[1])
      const endHour = parseInt(endParts[0])
      const endMin = parseInt(endParts[1])

      let startMinutes = startHour * 60 + startMin
      let endMinutes = endHour * 60 + endMin

      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60
      }

      const totalMinutes = endMinutes - startMinutes

      const rawHours = totalMinutes / 60.0
      hours = Math.round(rawHours * 4) / 4.0
    }

    if (hoursSpan) {
      hoursSpan.textContent = this.decimalToTimeFormat(hours)
    }

    return hours
  }

  calculateTotalHours() {
    let total = 0

    this.entryDailyHoursTargets.forEach(span => {
      const timeText = span.textContent.trim()
      if (timeText && timeText !== '0:00') {
        const parts = timeText.split(':')
        if (parts.length === 2) {
          const hours = parseInt(parts[0]) || 0
          const minutes = parseInt(parts[1]) || 0
          total += hours + (minutes / 60.0)
        } else {
          const decimal = parseFloat(timeText)
          if (!isNaN(decimal)) {
            total += decimal
          }
        }
      }
    })

    if (this.hasTotalHoursTarget) {
      this.totalHoursTarget.textContent = this.decimalToTimeFormat(total)
    }

    if (this.hasTotalHoursDisplayTarget) {
      this.totalHoursDisplayTarget.textContent = this.decimalToTimeFormat(total)
    }

    return total
  }

  calculateHoursForEntry(entryId) {
    const startInput = this.element.querySelector(`[data-timesheets-target="entryStartTime"][data-entry-id="${entryId}"]`)
    const endInput = this.element.querySelector(`[data-timesheets-target="entryEndTime"][data-entry-id="${entryId}"]`)
    const reasonSelect = this.element.querySelector(`[data-timesheets-target="entryAbsenceReason"][data-entry-id="${entryId}"]`)

    let hours = 0

    const hasStartTime = startInput?.value && startInput.value.trim() !== ''
    const hasEndTime = endInput?.value && endInput.value.trim() !== ''
    const hasAbsenceReason = reasonSelect?.value && reasonSelect.value.trim() !== ''

    if (hasStartTime && hasEndTime && !hasAbsenceReason) {
      const startParts = startInput.value.split(':')
      const endParts = endInput.value.split(':')

      const startHour = parseInt(startParts[0])
      const startMin = parseInt(startParts[1])
      const endHour = parseInt(endParts[0])
      const endMin = parseInt(endParts[1])

      let startMinutes = startHour * 60 + startMin
      let endMinutes = endHour * 60 + endMin

      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60
      }

      const totalMinutes = endMinutes - startMinutes

      const rawHours = totalMinutes / 60.0
      hours = Math.round(rawHours * 4) / 4.0
    }

    const hoursSpan = this.element.querySelector(`[data-timesheets-target="entryDailyHours"][data-entry-id="${entryId}"]`)
    if (hoursSpan) {
      hoursSpan.textContent = this.decimalToTimeFormat(hours)
    }

    return hours
  }

  initializeHours() {
    let originalTotalDisplay = null
    if (this.hasTotalHoursDisplayTarget) {
      originalTotalDisplay = this.totalHoursDisplayTarget.textContent.trim()
    }

    this.entryDailyHoursTargets.forEach(span => {
      const currentText = span.textContent.trim()
      const entryId = span.dataset.entryId

      if (currentText && !currentText.includes(':') && !isNaN(parseFloat(currentText))) {
        const decimal = parseFloat(currentText)
        span.textContent = this.decimalToTimeFormat(decimal)
      }

      if (entryId && entryId !== 'new') {
        const row = span.closest('tr')
        const startInput = row?.querySelector('[data-timesheets-target="entryStartTime"]')
        const endInput = row?.querySelector('[data-timesheets-target="entryEndTime"]')

        if (startInput && endInput && startInput.value && endInput.value && currentText === '0') {
        this.calculateHoursForEntry(entryId)
        }
      }
    })

    this.element.querySelectorAll('tr[data-working-day], tr[data-day-index]').forEach(row => {
        const hoursSpan = row.querySelector('[data-timesheets-target="entryDailyHours"]')
      if (hoursSpan) {
        const currentText = hoursSpan.textContent.trim()
        if (currentText && !currentText.includes(':') && !isNaN(parseFloat(currentText))) {
          const decimal = parseFloat(currentText)
          hoursSpan.textContent = this.decimalToTimeFormat(decimal)
        }
      }
    })

    if (!originalTotalDisplay || originalTotalDisplay === '0:00' || originalTotalDisplay === '0') {
      this.calculateTotalHours()
    }
  }

  async validateAndConfirmSubmission(event) {
    event.preventDefault()

    const validationResult = this.validateTimesheetEntries()

    if (!validationResult.isValid) {
      await errorDialog('Incomplete Timesheet', validationResult.message)
      return false
    }

    const totalHours = this.calculateTotalHours()

    const confirmed = await confirmDialog({
      title: 'Submit Timesheet',
      text: `Are you sure you want to submit this timesheet with ${this.decimalToTimeFormat(totalHours)}? This action cannot be undone.`,
      confirmButtonText: 'Yes, Submit Timesheet',
      cancelButtonText: 'Review Again'
    })

    if (confirmed) {
      event.target.form.submit()
    }
  }

  validateTimesheetEntries() {
    let emptyEntries = []
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    this.element.querySelectorAll('tr[data-day-index]').forEach(row => {
      const dayIndex = parseInt(row.dataset.dayIndex)
      const dayName = dayNames[dayIndex]

      const startInput = row.querySelector('[data-timesheets-target="entryStartTime"]')
      const endInput = row.querySelector('[data-timesheets-target="entryEndTime"]')
      const absenceSelect = row.querySelector('[data-timesheets-target="entryAbsenceReason"]')

      const hasStartTime = startInput?.value && startInput.value.trim() !== ''
      const hasEndTime = endInput?.value && endInput.value.trim() !== ''
      const hasAbsenceReason = absenceSelect?.value && absenceSelect.value.trim() !== ''

      if (hasAbsenceReason || (hasStartTime && hasEndTime)) {
      } else if (hasStartTime && !hasEndTime) {
        emptyEntries.push(`${dayName}: Missing end time`)
      } else if (!hasStartTime && hasEndTime) {
        emptyEntries.push(`${dayName}: Missing start time`)
      } else {
        emptyEntries.push(`${dayName}: Please fill in either working hours or an absence reason`)
      }
    })

    if (emptyEntries.length > 0) {
      return {
        isValid: false,
        message: 'Please fill in all days with either working hours or an absence reason.'
      }
    }

    return { isValid: true }
  }

  decimalToTimeFormat(decimalHours) {
    if (!decimalHours || decimalHours === 0) return "0:00"

    const hours = Math.floor(decimalHours)
    const minutes = Math.round((decimalHours - hours) * 60)

    const formattedHours = hours.toString()
    const formattedMinutes = minutes.toString().padStart(2, '0')

    return `${formattedHours}:${formattedMinutes}`
  }

  setupRejectionEventListeners() {
    document.addEventListener('click', (event) => {
      if (event.target.matches('#confirmRejection')) {
        event.preventDefault()
        this.submitRejection()
      }
    })

    $(document).on('hidden.bs.modal', '#timesheetRejectionModal', () => {
      this.resetRejectionForm()
    })
  }

  showRejectionModal(event) {
    const button = event.target.closest('button')
    let timesheetId, teacherName, weekStart, rejectUrl

    if (button && button.dataset.timesheetsTimesheetIdValue) {
      timesheetId = button.dataset.timesheetsTimesheetIdValue
      teacherName = button.dataset.timesheetsTeacherNameValue
      weekStart = button.dataset.timesheetsWeekStartValue
      rejectUrl = button.dataset.timesheetsRejectUrlValue
    } else {
      timesheetId = this.timesheetIdValue
      teacherName = this.teacherNameValue
      weekStart = this.weekStartValue
      rejectUrl = this.rejectUrlValue
    }

    if (!timesheetId) {
      this.showRejectionError('Timesheet ID not found.')
      return
    }

    this.timesheetIdValue = timesheetId
    this.teacherNameValue = teacherName || 'Unknown Teacher'
    this.weekStartValue = weekStart || 'Unknown Week'
    this.rejectUrlValue = rejectUrl

    const modalTitle = document.getElementById('timesheetRejectionModalLabel')
    if (modalTitle) {
      modalTitle.textContent = `Reject Timesheet - ${this.teacherNameValue} (${this.weekStartValue})`
    }

    $("#timesheetRejectionModal").modal("show")
  }

  async submitRejection() {
    const notes = document.getElementById('rejectionNotes')
    const confirmButton = document.getElementById('confirmRejection')

    if (!notes.value.trim()) {
      this.showRejectionError('Please provide a reason for rejecting this timesheet.')
      return
    }

    if (!this.rejectUrlValue) {
      this.showRejectionError('Reject URL not found.')
      return
    }

    confirmButton.disabled = true
    confirmButton.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Rejecting...'

    try {
      const response = await fetch(this.rejectUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({
          timesheet: {
            rejection_notes: notes.value.trim()
          }
        })
      })

      if (response.ok || response.status === 404) {
        $("#timesheetRejectionModal").modal("hide")
        this.showRejectionSuccess('Timesheet rejected successfully.')
        setTimeout(() => {
          if (window.location.pathname.match(/\/timesheets\/\d+$/)) {
            window.location.href = '/timesheets'
          } else {
            window.location.reload()
          }
        }, 1500)
      } else {
        this.showRejectionError('Failed to reject timesheet. Please try again.')
      }
    } catch (error) {
      this.showRejectionError('An error occurred while rejecting the timesheet.')
    } finally {
      confirmButton.disabled = false
      confirmButton.innerHTML = 'Reject Timesheet'
    }
  }

  resetRejectionForm() {
    const form = document.getElementById('timesheetRejectionForm')
    if (form) form.reset()
    this.removeExistingRejectionAlerts()
  }

  showRejectionError(message) {
    this.removeExistingRejectionAlerts()
    const alert = document.createElement('div')
    alert.className = 'alert alert-danger alert-dismissible fade show'
    alert.innerHTML = `${message}<button type="button" class="btn-close" data-dismiss="alert"><span>&times;</span></button>`
    document.querySelector('#timesheetRejectionModal .modal-body').insertBefore(alert, document.querySelector('#timesheetRejectionModal .modal-body').firstChild)
  }

  showRejectionSuccess(message) {
    const alert = document.createElement('div')
    alert.className = 'alert alert-success alert-dismissible fade show'
    alert.innerHTML = `${message}<button type="button" class="btn-close" data-dismiss="alert"><span>&times;</span></button>`
    document.querySelector('.main-content__header').insertBefore(alert, document.querySelector('.main-content__header').firstChild)
  }

  removeExistingRejectionAlerts() {
    document.querySelectorAll('#timesheetRejectionModal .alert').forEach(alert => alert.remove())
  }
}
